import React from 'react';

import styled from 'styled-components';

import {
  ACTIVITY_DIRECTION_TYPE,
  ACTIVITY_EMAIL_RECIPIENT_ROLE,
  ACTIVITY_TYPE_SLUG,
} from 'components/Activities/constants';
import { CommunityActivityRecipientType, CommunityActivityType } from 'types/communities';

type ActivitiesSidebarProps = {
  activities: CommunityActivityType[];
  selectedEmail?: string | null;
  onSelectEmail: (email: string) => void;
};

type SidebarParticipant = {
  email: string;
  label?: string | null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 260px;
  min-width: 260px;
  height: 100%;
  border-left: 1px solid var(--line-gray-bold);
  padding: 12px 12px 12px 0;
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 100%;
  padding: 18px 16px;
  overflow-y: auto;
  box-sizing: border-box;
  background: var(--background-gray-soft);
`;

const Title = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-family: var(--font-bold);
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ParticipantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ParticipantItem = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  background: var(--white);
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: var(--line-gray-bold);
  }

  ${({ active }) =>
    active
      ? `
    border-color: var(--dark-blue);
    background: #f5f8ff;
  `
      : ''}
`;

const ParticipantLabel = styled.div`
  font-family: var(--font-bold);
  font-size: 13px;
  color: var(--text-primary);
`;

const ParticipantEmail = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  word-break: break-word;
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const isSenderRecipient = (recipient: CommunityActivityRecipientType) => {
  return recipient.role === ACTIVITY_EMAIL_RECIPIENT_ROLE.FROM;
};

const sortParticipants = (participants: SidebarParticipant[]) =>
  participants.sort((firstParticipant, secondParticipant) => {
    const firstValue = (firstParticipant.label || firstParticipant.email).toLowerCase();
    const secondValue = (secondParticipant.label || secondParticipant.email).toLowerCase();

    return firstValue.localeCompare(secondValue);
  });

const addParticipant = (
  participantsByEmail: Map<string, SidebarParticipant>,
  participant: CommunityActivityRecipientType
) => {
  const normalizedEmail = participant.email.trim().toLowerCase();
  const existingParticipant = participantsByEmail.get(normalizedEmail);

  if (existingParticipant?.label || !participant.label) {
    if (!existingParticipant) {
      participantsByEmail.set(normalizedEmail, { email: normalizedEmail, label: null });
    }
    return;
  }

  participantsByEmail.set(normalizedEmail, {
    email: normalizedEmail,
    label: participant.label,
  });
};

const getConversationParticipants = (activities: CommunityActivityType[]) => {
  const inboundParticipantsByEmail = new Map<string, SidebarParticipant>();
  const outboundParticipantsByEmail = new Map<string, SidebarParticipant>();

  activities.forEach((activity) => {
    if (activity.activity_type_slug !== ACTIVITY_TYPE_SLUG.EMAIL || !activity.email) {
      return;
    }

    activity.email.recipients.filter(isSenderRecipient).forEach((recipient) => {
      if (activity.direction_type === ACTIVITY_DIRECTION_TYPE.INBOUND) {
        addParticipant(inboundParticipantsByEmail, recipient);
        return;
      }

      if (activity.direction_type === ACTIVITY_DIRECTION_TYPE.OUTBOUND) {
        addParticipant(outboundParticipantsByEmail, recipient);
      }
    });
  });

  return {
    inboundParticipants: sortParticipants(Array.from(inboundParticipantsByEmail.values())),
    outboundParticipants: sortParticipants(Array.from(outboundParticipantsByEmail.values())),
  };
};

const renderParticipants = (
  participants: SidebarParticipant[],
  selectedEmail: string | null | undefined,
  onSelectEmail: (email: string) => void
) => {
  if (participants.length === 0) {
    return <EmptyState>No emails found.</EmptyState>;
  }

  return (
    <ParticipantList>
      {participants.map((participant) => (
        <ParticipantItem
          key={participant.email}
          type="button"
          active={selectedEmail === participant.email}
          onClick={() => onSelectEmail(participant.email)}
        >
          {participant.label && <ParticipantLabel>{participant.label}</ParticipantLabel>}
          <ParticipantEmail>{participant.email}</ParticipantEmail>
        </ParticipantItem>
      ))}
    </ParticipantList>
  );
};

const ActivitiesSidebar = ({ activities, selectedEmail, onSelectEmail }: ActivitiesSidebarProps) => {
  const { inboundParticipants, outboundParticipants } = getConversationParticipants(activities);

  return (
    <Container>
      <Content className="mini-scroll">
        <Title>Conversation Emails</Title>
        <Section>
          <SectionTitle>Inbound</SectionTitle>
          {renderParticipants(inboundParticipants, selectedEmail, onSelectEmail)}
        </Section>
        <Section>
          <SectionTitle>Outbound</SectionTitle>
          {renderParticipants(outboundParticipants, selectedEmail, onSelectEmail)}
        </Section>
      </Content>
    </Container>
  );
};

export default ActivitiesSidebar;
